import { Mushroom } from '../entities/Mushroom';
import { RenderManager } from '../../../core/RenderManager';

export class MushroomField {
  private width: number;
  private height: number;
  private gridSize: number;
  private mushrooms: Mushroom[] = [];
  private mushroomGrid: (Mushroom | null)[][] = [];
  private cols: number;
  private rows: number;
  private renderManager: RenderManager;

  constructor(width: number, height: number, gridSize: number, renderManager: RenderManager) {
    this.width = width;
    this.height = height;
    this.gridSize = gridSize;
    this.renderManager = renderManager;
    this.cols = Math.floor(width / gridSize);
    this.rows = Math.floor(height / gridSize);
    
    // Initialize empty grid
    this.mushroomGrid = Array(this.rows).fill(null).map(() => Array(this.cols).fill(null));
  }

  public generateField(): void {
    this.mushrooms = [];
    this.mushroomGrid = Array(this.rows).fill(null).map(() => Array(this.cols).fill(null));

    // Generate mushrooms in the main play area (not in player zone at bottom)
    const playerZoneRows = 6; // Bottom 6 rows reserved for player
    const playAreaRows = this.rows - playerZoneRows;

    // Create random mushrooms throughout the play area
    const mushroomDensity = 0.08; // 8% chance per grid cell
    let mushroomId = 1;

    for (let row = 1; row < playAreaRows; row++) { // Start from row 1 to leave top clear
      for (let col = 1; col < this.cols - 1; col++) { // Leave edges clear
        if (Math.random() < mushroomDensity) {
          const x = col * this.gridSize;
          const y = row * this.gridSize;
          
          const mushroom = new Mushroom(
            `mushroom_${mushroomId++}`,
            x,
            y,
            this.renderManager
          );
          
          this.mushrooms.push(mushroom);
          this.mushroomGrid[row][col] = mushroom;
        }
      }
    }

    console.log(`Generated ${this.mushrooms.length} mushrooms`);
  }

  public regenerateField(): void {
    // Keep existing mushrooms but add some new ones
    const mushroomDensity = 0.04; // Lower density for regeneration
    const playerZoneRows = 6;
    const playAreaRows = this.rows - playerZoneRows;
    let mushroomId = this.mushrooms.length + 1000;

    for (let row = 1; row < playAreaRows; row++) {
      for (let col = 1; col < this.cols - 1; col++) {
        // Only add mushroom if slot is empty
        if (!this.mushroomGrid[row][col] && Math.random() < mushroomDensity) {
          const x = col * this.gridSize;
          const y = row * this.gridSize;
          
          const mushroom = new Mushroom(
            `mushroom_${mushroomId++}`,
            x,
            y,
            this.renderManager
          );
          
          this.mushrooms.push(mushroom);
          this.mushroomGrid[row][col] = mushroom;
        }
      }
    }
    
    // Also repair some existing damaged mushrooms
    this.mushrooms.forEach(mushroom => {
      if (mushroom.active && !mushroom.isFullHealth() && Math.random() < 0.3) {
        mushroom.repair();
      }
    });
  }

  public getMushrooms(): Mushroom[] {
    return this.mushrooms;
  }

  public getMushroomAt(x: number, y: number): Mushroom | null {
    const col = Math.floor(x / this.gridSize);
    const row = Math.floor(y / this.gridSize);
    
    if (row >= 0 && row < this.rows && col >= 0 && col < this.cols) {
      return this.mushroomGrid[row][col];
    }
    
    return null;
  }

  public addMushroom(x: number, y: number): void {
    // Snap to grid
    const col = Math.floor(x / this.gridSize);
    const row = Math.floor(y / this.gridSize);
    
    if (row >= 0 && row < this.rows && col >= 0 && col < this.cols && !this.mushroomGrid[row][col]) {
      const snapX = col * this.gridSize;
      const snapY = row * this.gridSize;
      
      const mushroom = new Mushroom(
        `mushroom_${Date.now()}`,
        snapX,
        snapY,
        this.renderManager
      );
      
      this.mushrooms.push(mushroom);
      this.mushroomGrid[row][col] = mushroom;
    }
  }

  public removeMushroom(mushroom: Mushroom): void {
    const col = Math.floor(mushroom.position.x / this.gridSize);
    const row = Math.floor(mushroom.position.y / this.gridSize);
    
    if (row >= 0 && row < this.rows && col >= 0 && col < this.cols) {
      this.mushroomGrid[row][col] = null;
    }
    
    const index = this.mushrooms.indexOf(mushroom);
    if (index !== -1) {
      this.mushrooms.splice(index, 1);
    }
  }

  public isBlocked(x: number, y: number): boolean {
    const mushroom = this.getMushroomAt(x, y);
    return mushroom !== null && mushroom.active;
  }

  public getGridSize(): number {
    return this.gridSize;
  }

  public getGridPosition(x: number, y: number): { col: number; row: number } {
    return {
      col: Math.floor(x / this.gridSize),
      row: Math.floor(y / this.gridSize)
    };
  }

  public getWorldPosition(col: number, row: number): { x: number; y: number } {
    return {
      x: col * this.gridSize,
      y: row * this.gridSize
    };
  }
}